import { j as jsxRuntimeExports } from "./jsx-runtime-ClFauRgV.mjs";
import React__default from "react";
import { styled, css } from "styled-components";
import { T as TOKENS, S as STATIC_TOKENS, f as fontSizeRem, u as useKindredConfigOptional } from "./KindredConfigProvider-BCI_FXKD.mjs";
const HEADLINE_ELEMENTS = ["h1", "h2", "h3", "h4", "h5", "h6"];
const HEADLINE_SIZES = ["large", "medium", "small", "xSmall"];
const HEADLINE_LETTER_SPACING = {
  large: TOKENS.text.letterSpacing.headlineLarge,
  medium: TOKENS.text.letterSpacing.headlineMedium,
  small: TOKENS.text.letterSpacing.headlineSmall,
  xSmall: TOKENS.text.letterSpacing.headlineXsmall
};
const HEADLINE_FONT_SIZE_BY_SIZE = {
  large: {
    mobile: fontSizeRem("52"),
    tablet: fontSizeRem("56"),
    desktop: fontSizeRem("64")
  },
  medium: {
    mobile: fontSizeRem("44"),
    tablet: fontSizeRem("48"),
    desktop: fontSizeRem("48")
  },
  small: {
    mobile: fontSizeRem("36"),
    tablet: fontSizeRem("40"),
    desktop: fontSizeRem("40")
  },
  xSmall: {
    mobile: fontSizeRem("28"),
    tablet: fontSizeRem("32"),
    desktop: fontSizeRem("32")
  }
};
const headlineStyles = ({
  responsive = true
} = {}) => css`
    color: var(${TOKENS.color.foreground.primary});
    font-family: var(${TOKENS.text.fontFamily.accent});
    font-weight: var(${TOKENS.text.fontWeight.headline});
    line-height: 110%;
    margin: 0;

    ${({
  $size
}) => `
      font-size: ${HEADLINE_FONT_SIZE_BY_SIZE[$size].desktop};
      letter-spacing: var(${HEADLINE_LETTER_SPACING[$size]});
    `}

    ${({
  $size
}) => responsive ? `
          /* Desktop-first step-downs. Larger breakpoint first so mobile wins below sm. */
          @media (max-width: ${STATIC_TOKENS.breakpoint.md}) {
            font-size: ${HEADLINE_FONT_SIZE_BY_SIZE[$size].tablet};
          }

          @media (max-width: ${STATIC_TOKENS.breakpoint.sm}) {
            font-size: ${HEADLINE_FONT_SIZE_BY_SIZE[$size].mobile};
          }
        ` : ""}
  `;
const StyledHeadline = styled.h1`
  ${headlineStyles()}
`;
const Headline = React__default.forwardRef(({
  as,
  size,
  children,
  ...props
}, ref) => /* @__PURE__ */ jsxRuntimeExports.jsx(StyledHeadline, { ...props, $size: size, as, "data-kindred": "headline", ref, children }));
const HEADER_ELEMENTS = ["h1", "h2", "h3", "h4", "h5", "h6", "p", "span"];
const HEADER_SIZES = ["xLarge", "large", "medium", "small", "xSmall"];
const HEADER_COLORS = ["default", "subdued"];
const HEADER_FONT_SIZE_BY_SIZE = {
  xLarge: {
    mobile: fontSizeRem("22"),
    tablet: fontSizeRem("32"),
    desktop: fontSizeRem("32")
  },
  large: {
    mobile: fontSizeRem("21"),
    tablet: fontSizeRem("24"),
    desktop: fontSizeRem("24")
  },
  medium: {
    mobile: fontSizeRem("18"),
    tablet: fontSizeRem("20"),
    desktop: fontSizeRem("20")
  },
  small: {
    mobile: fontSizeRem("16"),
    tablet: fontSizeRem("16"),
    desktop: fontSizeRem("16")
  },
  xSmall: {
    mobile: fontSizeRem("14"),
    tablet: fontSizeRem("14"),
    desktop: fontSizeRem("14")
  }
};
const HEADER_LINE_HEIGHT_BY_SIZE = {
  xLarge: 120,
  large: 120,
  medium: 130,
  small: 140,
  xSmall: 140
};
const headerStyles = ({
  responsive = true
} = {}) => css`
    font-family: var(${TOKENS.text.fontFamily.primary});
    font-weight: var(${TOKENS.text.fontWeight.header});
    margin: 0;

    ${({
  $color = "default"
}) => `
      color: var(${$color === "subdued" ? TOKENS.color.foreground.subdued : TOKENS.color.foreground.default});
    `}

    ${({
  $size
}) => `
      font-size: ${HEADER_FONT_SIZE_BY_SIZE[$size].desktop};
      line-height: ${HEADER_LINE_HEIGHT_BY_SIZE[$size]}%;
    `}

    ${({
  $size
}) => responsive ? `
          /* Desktop-first step-downs. Larger breakpoint first so mobile wins below sm. */
          @media (max-width: ${STATIC_TOKENS.breakpoint.md}) {
            font-size: ${HEADER_FONT_SIZE_BY_SIZE[$size].tablet};
          }

          @media (max-width: ${STATIC_TOKENS.breakpoint.sm}) {
            font-size: ${HEADER_FONT_SIZE_BY_SIZE[$size].mobile};
          }
        ` : ""}
  `;
const StyledHeader = styled.h2`
  ${headerStyles()}
`;
const Header = React__default.forwardRef(({
  as,
  color = "default",
  size,
  children,
  ...props
}, ref) => /* @__PURE__ */ jsxRuntimeExports.jsx(StyledHeader, { ...props, $color: color, $size: size, as, "data-kindred": "header", ref, children }));
const BODY_ELEMENTS = ["p", "span"];
const BODY_SIZES = ["xLarge", "large", "medium", "small", "xSmall"];
const BODY_COLORS = ["default", "subdued"];
const BODY_FONT_SIZE_BY_SIZE = {
  xLarge: {
    mobile: fontSizeRem("21"),
    tablet: fontSizeRem("24"),
    desktop: fontSizeRem("24")
  },
  large: {
    mobile: fontSizeRem("18"),
    tablet: fontSizeRem("20"),
    desktop: fontSizeRem("20")
  },
  medium: {
    mobile: fontSizeRem("16"),
    tablet: fontSizeRem("16"),
    desktop: fontSizeRem("16")
  },
  small: {
    mobile: fontSizeRem("14"),
    tablet: fontSizeRem("14"),
    desktop: fontSizeRem("14")
  },
  xSmall: {
    mobile: fontSizeRem("12"),
    tablet: fontSizeRem("12"),
    desktop: fontSizeRem("12")
  }
};
const BODY_LINE_HEIGHT_BY_SIZE = {
  xLarge: 160,
  large: 150,
  medium: 160,
  small: 140,
  xSmall: 140
};
const bodyStyles = ({
  responsive = true
} = {}) => css`
    font-family: var(${TOKENS.text.fontFamily.primary});
    font-weight: var(${TOKENS.text.fontWeight.default});
    margin: 0;

    ${({
  $color = "default"
}) => `
      color: var(${$color === "subdued" ? TOKENS.color.foreground.subdued : TOKENS.color.foreground.default});
    `}

    ${({
  $size
}) => `
      font-size: ${BODY_FONT_SIZE_BY_SIZE[$size].desktop};
      line-height: ${BODY_LINE_HEIGHT_BY_SIZE[$size]}%;
    `}

    ${({
  $size
}) => responsive ? `
          /* Desktop-first step-downs. Larger breakpoint first so mobile wins below sm. */
          @media (max-width: ${STATIC_TOKENS.breakpoint.md}) {
            font-size: ${BODY_FONT_SIZE_BY_SIZE[$size].tablet};
          }

          @media (max-width: ${STATIC_TOKENS.breakpoint.sm}) {
            font-size: ${BODY_FONT_SIZE_BY_SIZE[$size].mobile};
          }
        ` : ""}
  `;
const StyledBody = styled.p`
  ${bodyStyles()}
`;
const Body = React__default.forwardRef(({
  as,
  color = "default",
  size,
  children,
  ...props
}, ref) => /* @__PURE__ */ jsxRuntimeExports.jsx(StyledBody, { ...props, $color: color, $size: size, as, "data-kindred": "body", ref, children }));
const LABEL_ELEMENTS = ["p", "span"];
const LABEL_SIZES = ["large", "medium", "small", "xSmall"];
const LABEL_COLORS = ["default", "subdued"];
const LABEL_FONT_SIZE_BY_SIZE = {
  large: {
    mobile: fontSizeRem("16"),
    tablet: fontSizeRem("18"),
    desktop: fontSizeRem("18")
  },
  medium: {
    mobile: fontSizeRem("14"),
    tablet: fontSizeRem("16"),
    desktop: fontSizeRem("16")
  },
  small: {
    mobile: fontSizeRem("12"),
    tablet: fontSizeRem("14"),
    desktop: fontSizeRem("14")
  },
  xSmall: {
    mobile: fontSizeRem("10"),
    tablet: fontSizeRem("12"),
    desktop: fontSizeRem("12")
  }
};
const labelStyles = ({
  responsive = true
} = {}) => css`
    font-family: var(${TOKENS.text.fontFamily.primary});
    font-weight: var(${TOKENS.text.fontWeight.label});
    line-height: 140%;
    /* 12% of the font size, converted to em */
    letter-spacing: 0.12em;
    margin: 0;

    ${({
  $color = "default"
}) => `
      color: var(${$color === "subdued" ? TOKENS.color.foreground.subdued : TOKENS.color.foreground.default});
    `}

    ${({
  $size
}) => `
      font-size: ${LABEL_FONT_SIZE_BY_SIZE[$size].desktop};
    `}

    ${({
  $size
}) => responsive ? `
          /* Desktop-first step-downs. Larger breakpoint first so mobile wins below sm. */
          @media (max-width: ${STATIC_TOKENS.breakpoint.md}) {
            font-size: ${LABEL_FONT_SIZE_BY_SIZE[$size].tablet};
          }

          @media (max-width: ${STATIC_TOKENS.breakpoint.sm}) {
            font-size: ${LABEL_FONT_SIZE_BY_SIZE[$size].mobile};
          }
        ` : ""}
  `;
const StyledLabel = styled.p`
  ${labelStyles()}

  /* Carrier-configurable via the Kindred config's misc.labelAllCaps. */
  text-transform: ${({
  $allCaps
}) => $allCaps ? "uppercase" : "none"};
`;
const Label = React__default.forwardRef(({
  as,
  color = "default",
  size,
  children,
  ...props
}, ref) => {
  var _a, _b;
  const labelAllCaps = ((_b = (_a = useKindredConfigOptional()) == null ? void 0 : _a.misc) == null ? void 0 : _b.labelAllCaps) ?? false;
  return /* @__PURE__ */ jsxRuntimeExports.jsx(StyledLabel, { ...props, $allCaps: labelAllCaps, $color: color, $size: size, as, "data-kindred": "label", ref, children });
});
const DISCLAIMER_ELEMENTS = ["p", "span"];
const DISCLAIMER_SIZES = ["medium", "small", "xSmall"];
const DISCLAIMER_FONT_SIZE_BY_SIZE = {
  medium: {
    mobile: fontSizeRem("14"),
    tablet: fontSizeRem("14"),
    desktop: fontSizeRem("14")
  },
  small: {
    mobile: fontSizeRem("11"),
    tablet: fontSizeRem("11"),
    desktop: fontSizeRem("11")
  },
  xSmall: {
    mobile: fontSizeRem("10"),
    tablet: fontSizeRem("10"),
    desktop: fontSizeRem("10")
  }
};
const DISCLAIMER_LINE_HEIGHT_BY_SIZE = {
  medium: 150,
  small: 160,
  xSmall: 140
};
const disclaimerStyles = ({
  responsive = true
} = {}) => css`
    color: var(${TOKENS.color.foreground.default});
    font-family: var(${TOKENS.text.fontFamily.primary});
    font-weight: var(${TOKENS.text.fontWeight.disclaimer});
    margin: 0;

    ${({
  $size
}) => `
      font-size: ${DISCLAIMER_FONT_SIZE_BY_SIZE[$size].desktop};
      line-height: ${DISCLAIMER_LINE_HEIGHT_BY_SIZE[$size]}%;
    `}

    ${({
  $size
}) => responsive ? `
          /* Desktop-first step-downs. Larger breakpoint first so mobile wins below sm. */
          @media (max-width: ${STATIC_TOKENS.breakpoint.md}) {
            font-size: ${DISCLAIMER_FONT_SIZE_BY_SIZE[$size].tablet};
          }

          @media (max-width: ${STATIC_TOKENS.breakpoint.sm}) {
            font-size: ${DISCLAIMER_FONT_SIZE_BY_SIZE[$size].mobile};
          }
        ` : ""}
  `;
const StyledDisclaimer = styled.p`
  ${disclaimerStyles()}
`;
const Disclaimer = React__default.forwardRef(({
  as,
  size,
  children,
  ...props
}, ref) => /* @__PURE__ */ jsxRuntimeExports.jsx(StyledDisclaimer, { ...props, $size: size, as, "data-kindred": "disclaimer", ref, children }));
const SIGNATURE_TEXT_FONT_SIZE = {
  mobile: fontSizeRem("21"),
  tablet: fontSizeRem("24"),
  desktop: fontSizeRem("24")
};
const signatureTextStyles = ({
  responsive = true
} = {}) => css`
  font-family: var(${TOKENS.text.fontFamily.signature});
  font-weight: 400;
  font-size: ${SIGNATURE_TEXT_FONT_SIZE.desktop};
  letter-spacing: 0.6px;
  line-height: 140%;

  ${responsive ? `
      /* Desktop-first step-downs. Larger breakpoint first so mobile wins below sm. */
      @media (max-width: ${STATIC_TOKENS.breakpoint.md}) {
        font-size: ${SIGNATURE_TEXT_FONT_SIZE.tablet};
      }

      @media (max-width: ${STATIC_TOKENS.breakpoint.sm}) {
        font-size: ${SIGNATURE_TEXT_FONT_SIZE.mobile};
      }
    ` : ""}
`;
const StyledSignatureText = styled.span`
  ${signatureTextStyles()}
`;
const SignatureText = React__default.forwardRef(({
  children,
  ...props
}, ref) => /* @__PURE__ */ jsxRuntimeExports.jsx(StyledSignatureText, { ...props, "data-kindred": "signature-text", ref, children }));
export {
  BODY_COLORS,
  BODY_ELEMENTS,
  BODY_SIZES,
  Body,
  DISCLAIMER_ELEMENTS,
  DISCLAIMER_SIZES,
  Disclaimer,
  HEADER_COLORS,
  HEADER_ELEMENTS,
  HEADER_SIZES,
  HEADLINE_ELEMENTS,
  HEADLINE_SIZES,
  Header,
  Headline,
  LABEL_COLORS,
  LABEL_ELEMENTS,
  LABEL_SIZES,
  Label,
  SignatureText,
  bodyStyles,
  disclaimerStyles,
  headerStyles,
  headlineStyles,
  labelStyles,
  signatureTextStyles
};
